export type EventPayload = Record<string, unknown> | unknown;

export type EventHandler = (event: Event) => void;

// Single source of truth for all events sent/received

export const GAME_CLIENT_EVENTS = {
  INITIALIZE_CLIENT: 'GC/INITIALIZE_HANDSHAKE',
  GAME_READY: 'GC/SET_GAME_LOADED',
  GAME_LOAD_PROGRESS: 'GC/SET_GAME_LOAD_PROGRESS',
  SPIN_IN_PROGRESS: 'GC/SET_SPIN_IN_PROGRESS',
  PROVIDER_ERROR: 'GC/SET_PROVIDER_ERROR',
  AUDIO_ENABLED: 'GC/SET_AUDIO_ENABLED',
  REQUEST_GAME_WINDOW_UI_STATE: 'GC/REQUEST_GAME_WINDOW_UI_STATE',
  SHOW_FUNDS: 'GC/SHOW_FUNDS',
  CLOSE_GAME_LAUNCHER: 'GC/CLOSE_GAME_LAUNCHER',
  RESET_GAME: 'GC/RESET_GAME',
  RESUME_INCOMPLETE_GAME: 'GC/RESUME_INCOMPLETE_GAME',
  GAME_SWITCH: 'GC/GAME_SWITCH',
  CONTEXT_PARAM_MISSING: 'GC/CONTEXT_PARAM_MISSING',
  CONTEXT_RECEIVED: 'GC/CONTEXT_RECEIVED',
} as const;

export const GAME_CLIENT_ERRORS = {
  USER_HAS_NOT_ACCEPTED_TERMS_AND_CONDITIONS:
    'USER_HAS_NOT_ACCEPTED_TERMS_AND_CONDITIONS',
} as const;

export const ANDROID_WRAPPER_EVENTS = {
  GAME_CLOSE: 'GAME_LAUNCHER_CLOSE',
  SHOW_DEPOSIT: 'SHOW_DEPOSIT',
  OPEN_EXTERNAL_WEBVIEW: 'OPEN_EXTERNAL_WEBVIEW',
  RELOAD_GAME: 'RELOAD_GAME',
  CLOSE_REALITY_CHECK_MODAL: 'CLOSE_REALITY_CHECK_MODAL',
  REALITY_CHECK_MODAL_EVENT: 'REALITY_CHECK_MODAL_EVENT',
  REQUEST_GAME_CLOSE: 'REQUEST_GAME_CLOSE',
  DEVICE_ORIENTATION_INFO: 'DEVICE_ORIENTATION_INFO',
  MODULAR_DEPOSIT_DISMISSED: 'MODULAR_DEPOSIT_DISMISSED',
  REFRESH_RECENTLY_PLAYED: 'REFRESH_RECENTLY_PLAYED',
} as const;

export const SESSION_EVENTS = {
  /** Account UM refreshed its shared session cookies. */
  TOKENS_UPDATE: 'AW/UPDATE_TOKENS',
  /** Account UM invalidated the current session. */
  SESSION_INVALIDATED: 'AW/SESSION_INVALIDATED',
} as const;

/** Same-origin messages posted to the casino-next lobby when embedded in its iframe. */
export const LOBBY_EVENTS = {
  REFRESH_RECENTLY_PLAYED: 'REFRESH_RECENTLY_PLAYED',
} as const;

export const GAME_WINDOW_TO_CLIENT_EVENTS = {
  GOLOCATION_RESULT: 'GL/GEOLOCATION_RESULT',
  GEOLOCATION_UPDATED: 'GL/GEOLOCATION_ANDROID_TOKEN_UPDATED',
  RELOAD_BALANCE: 'GL/RELOAD_BALANCE',
  TOGGLE_PAYTABLE: 'GL/TOGGLE_PAYTABLE',
  TOGGLE_AUDIO: 'GL/TOGGLE_AUDIO',
  AUTO_SPIN_STOP: 'GL/AUTO_SPIN_STOP',
  AUTO_SPIN_RESUME: 'GL/AUTO_SPIN_RESUME',
  TOGGLE_HELP: 'GL/TOGGLE_HELP',
  DISMISS_ERROR: 'GL/DISMISS_ERROR',
  FREE_SPINS_DIALOG: 'GL/FREE_SPINS_DIALOG',
  AUTHENTICATION_TOKEN_UPDATE: 'GL/AUTHENTICATION_TOKEN_UPDATE',
  INITIALIZE_URL: 'GL/INITIALIZE_URL',
  GAME_WINDOW_UI_ENABLED: 'GL/GAME_WINDOW_UI_ENABLED',
} as const;

export type GameClientEvent =
  (typeof GAME_CLIENT_EVENTS)[keyof typeof GAME_CLIENT_EVENTS];
export type AndroidWrapperEvent =
  (typeof ANDROID_WRAPPER_EVENTS)[keyof typeof ANDROID_WRAPPER_EVENTS];
export type SessionEvent = (typeof SESSION_EVENTS)[keyof typeof SESSION_EVENTS];
export type GameWindowToClientEvent =
  (typeof GAME_WINDOW_TO_CLIENT_EVENTS)[keyof typeof GAME_WINDOW_TO_CLIENT_EVENTS];
export type CommonEventType =
  | GameClientEvent
  | AndroidWrapperEvent
  | SessionEvent
  | GameWindowToClientEvent;

export const EVENT_NAMES = {
  GEOLOCATION_RESULT: 'geolocationResult',
  GEOLOCATION_UPDATE: 'geolocationTokenUpdated',
} as const;

export type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];
